import { useState, type FormEvent } from "react";
import {
  completePasswordReset,
  signIn,
  startPasswordReset,
  type SignedIn,
} from "../lib/auth";
import { authProblem } from "../lib/authMessages";
import { Spinner } from "../components/Spinner";
import { IconArrowLeft } from "../ui/icons";

/* Reset a password with a code Cognito emails.
 *
 * Two steps, because they are two different waits and two different failures.
 * Asking for a code can fail on an account that does not exist. Using one can
 * fail on a wrong code, an expired code, or a password the pool refuses, and
 * none of those should send somebody back to the first step to type their
 * email again.
 *
 * The last step signs them in rather than returning them to the sign-in form.
 * Cognito's confirmPassword sets the password and issues no session, so
 * without this a person types their new password, is told it worked, and has
 * to type it again. The password is already in hand here, so this asks once.
 */

type Step = "ask" | "code";

export function PasswordReset({
  initialEmail,
  onSignedIn,
  onCancel,
}: {
  initialEmail: string;
  onSignedIn: (s: SignedIn) => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [step, setStep] = useState<Step>("ask");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function sendCode(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setProblem(null);
    setNote(null);
    try {
      await startPasswordReset(email.trim().toLowerCase());
      setStep("code");
      setNote(`A code is on its way to ${email.trim().toLowerCase()}. Check your spam folder.`);
    } catch (err) {
      setProblem(authProblem(err, "reset"));
    } finally {
      setBusy(false);
    }
  }

  async function setNewPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    setNote(null);
    const address = email.trim().toLowerCase();
    try {
      await completePasswordReset(address, code.trim(), password);
    } catch (err) {
      setProblem(authProblem(err, "reset"));
      setBusy(false);
      return;
    }
    // The password is set from here on. A failure below is a failure to sign
    // in with a password that now works, so it must not read as a failed
    // reset and send somebody round the loop to burn a second code.
    try {
      onSignedIn(await signIn(address, password));
    } catch (err) {
      setProblem(
        `Your password is set. Signing in with it did not work: ${authProblem(err, "signin")}`,
      );
      setBusy(false);
    }
  }

  return (
    <main style={{ maxWidth: "22rem" }}>
      <h1>Reset your password</h1>

      {step === "ask" ? (
        <form onSubmit={sendCode}>
          <p className="muted">Cognito emails a code to your Buzze address.</p>
          <p>
            <label htmlFor="reset-email">Email</label><br />
            <input
              id="reset-email" type="email" autoComplete="username" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              style={{ width: "100%" }}
            />
          </p>
          <button type="submit" disabled={busy}>
            {busy ? <><Spinner />Sending</> : "Email me a code"}
          </button>
        </form>
      ) : (
        <form onSubmit={setNewPassword}>
          <p>
            <label htmlFor="reset-code">Code from the email</label><br />
            <input
              id="reset-code" inputMode="numeric" autoComplete="one-time-code" required
              value={code} onChange={(e) => setCode(e.target.value)}
              style={{ width: "100%" }}
            />
          </p>
          <p>
            <label htmlFor="reset-pw">New password</label><br />
            <input
              id="reset-pw" type="password" autoComplete="new-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              style={{ width: "100%" }}
            />
          </p>
          <button type="submit" disabled={busy}>
            {busy ? "Setting your password" : "Set password and sign in"}
          </button>
          {" "}
          <button type="button" className="link" onClick={() => sendCode()} disabled={busy}>
            Send another code
          </button>
        </form>
      )}

      {note ? <p className="muted" role="status">{note}</p> : null}
      {problem ? <p className="refusal" role="alert">{problem}</p> : null}

      <p>
        <button type="button" className="link ui-back" onClick={onCancel} disabled={busy}>
          <IconArrowLeft />Back to sign in
        </button>
      </p>
    </main>
  );
}
